import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, ChevronRight, Filter, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SupportFind — alphabetical list of sponsors a group can ask for support.
 *
 * Entry: /support/find?group_id=<gid> (creator/steward only).
 * Directive:
 * - alphabetical ONLY
 * - no ratings, reviews, metrics, featured placement, sorting
 * - optional category filter (simple, non-prominent)
 * - "asking, not shopping"
 */
export default function SupportFind() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const groupId = params.get('group_id');
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    setLoading(true);
    const url = filter ? `${API}/api/support/sponsors?category_id=${encodeURIComponent(filter)}` : `${API}/api/support/sponsors`;
    axios.get(url, { withCredentials: true })
      .then(r => setSponsors(r.data?.sponsors || []))
      .catch(() => setSponsors([]))
      .finally(() => setLoading(false));
  }, [filter]);

  // Category options drawn from what sponsors actually cover — no promotional order.
  const allCategories = Array.from(
    new Set(sponsors.flatMap(s => s.categories || []))
  ).sort();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }} data-testid="support-find-page">
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-2xl mx-auto px-4">
          <div className="flex items-center h-14 gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 hover:opacity-70" aria-label="Back" data-testid="support-find-back">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-base font-semibold truncate flex-1" style={{ color: 'var(--fy-text-primary)' }}>
              Find support for this group
            </h1>
            <button
              onClick={() => setShowFilter(!showFilter)}
              className="p-2 -mr-2 hover:opacity-70"
              aria-label="Filter"
              data-testid="support-find-filter-toggle"
            >
              <Filter className="w-4 h-4" style={{ color: filter ? 'var(--lk-sage)' : 'var(--fy-text-secondary)' }} />
            </button>
          </div>
          {showFilter && (
            <div className="pb-3 flex flex-wrap gap-1.5" data-testid="support-find-filters">
              <button
                onClick={() => setFilter('')}
                className="text-xs px-2.5 py-1 rounded-full"
                style={{
                  backgroundColor: !filter ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                  color: !filter ? '#fff' : 'var(--fy-text-secondary)',
                }}
                data-testid="support-filter-all"
              >
                All
              </button>
              {allCategories.map(c => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className="text-xs px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: filter === c ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                    color: filter === c ? '#fff' : 'var(--fy-text-secondary)',
                  }}
                  data-testid={`support-filter-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--fy-text-secondary)' }} />
          </div>
        ) : sponsors.length === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
              No sponsors to show yet.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="support-sponsor-list">
            {sponsors.map(s => (
              <li key={s.sponsor_id}>
                <Link
                  to={`/support/sponsor/${s.sponsor_id}${groupId ? `?group_id=${groupId}` : ''}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
                  data-testid={`support-sponsor-item-${s.sponsor_id}`}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
                    {s.logo_url ? (
                      <img src={s.logo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Users className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--fy-text-primary)' }}>
                      {s.name || 'Sponsor'}
                    </p>
                    {s.categories?.length > 0 && (
                      <p className="text-xs truncate" style={{ color: 'var(--fy-text-secondary)' }}>
                        {s.categories.join(' · ')}
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--fy-text-disabled)' }} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
